import {FormEvent, useEffect, useState} from "react";
import {Alert, Button, Card, Form, ProgressBar} from "react-bootstrap";
import {ProductsApi} from "../../../api/ProductsApi";
import {ProductViewModel} from "../../../api/models";
import {uploadToPresignedUrl} from "../../../api/upload";
import {ErrorAlert} from "../../../components/ErrorAlert";
import {ConfirmModal} from "../../../components/ConfirmModal";
import {SubmitButton} from "../../../components/SubmitButton";
import {RichTextEditor} from "../../../components/RichTextEditor";
import {formatBytes, formatDateTime} from "../../../components/format";

// The API's limit for the reviewer's notes.
export const PRESS_PDF_MAX_BYTES = 50 * 1024 * 1024;

// The product's press kit, on /press: the image pack (a zip the API builds from the images above, in their order, with
// their alt texts), the reviewer's notes PDF (uploaded straight to the CDN bucket, then confirmed), the feature summary,
// and the switch that shows it there (with the product published, too).
export function ProductPressCard({product, onChanged}: { product: ProductViewModel, onChanged(): void }) {
    const press = product.press;
    const [summary, setSummary] = useState(press?.summary ?? '');
    const [published, setPublished] = useState(press?.published ?? false);
    const [notesFile, setNotesFile] = useState<File>();
    const [progress, setProgress] = useState<number>();
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [removingNotes, setRemovingNotes] = useState(false);
    const [error, setError] = useState<unknown>();
    const [formKey, setFormKey] = useState(0);

    useEffect(() => {
        setSummary(product.press?.summary ?? '');
        setPublished(product.press?.published ?? false);
    }, [product]);

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            await action();
            onChanged();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setProgress(undefined);
        }
    };

    const notesProblem = !notesFile ? undefined
        : !/\.pdf$/i.test(notesFile.name) && notesFile.type !== 'application/pdf' ? 'Use a PDF.'
        : notesFile.size > PRESS_PDF_MAX_BYTES ? `Use a file of at most ${formatBytes(PRESS_PDF_MAX_BYTES)}.`
        : undefined;

    const uploadNotes = (e: FormEvent) => {
        e.preventDefault();
        if (!notesFile || notesProblem) {
            return;
        }
        void run(async () => {
            setProgress(0);
            const {upload} = await ProductsApi.startReviewerNotes(product.id, notesFile.name, notesFile.size);
            await uploadToPresignedUrl(upload, notesFile, setProgress);
            await ProductsApi.completeReviewerNotes(product.id);
            setNotesFile(undefined);
            setFormKey(x => x + 1); // clears the file input
        });
    };

    const save = (e: FormEvent) => {
        e.preventDefault();
        void run(async () => {
            await ProductsApi.updatePress(product.id, { summary: summary.trim(), published });
            setSaved(true);
        });
    };

    const pack = press?.imagePack;
    const notes = press?.reviewerNotes;

    return (
        <Card className="mt-4" data-testid="press-card">
            <Card.Header>Press kit</Card.Header>
            <Card.Body>
                <ErrorAlert error={error} onClose={() => setError(undefined)}/>

                <h3 className="h6">Image pack</h3>
                {pack ? (
                    <p className="small mb-2">
                        Built {formatDateTime(pack.uploadedOn)} from {pack.imageCount} {pack.imageCount === 1 ? 'image' : 'images'}:{' '}
                        <a href={pack.url}>{pack.fileName}</a> ({formatBytes(pack.fileSize)})
                    </p>
                ) : <p className="small text-body-secondary mb-2">Not built yet.</p>}
                {pack && !pack.upToDate && (
                    <Alert variant="warning" className="small py-2">
                        The images have changed since the pack was built. Build it again to bring it up to date.
                    </Alert>
                )}
                <Button variant="outline-primary" size="sm" disabled={busy || product.images.length === 0}
                        onClick={() => void run(() => ProductsApi.buildPressImagePack(product.id))}>
                    <i className="bi bi-file-earmark-zip me-1" aria-hidden="true"></i>Build Press Image Pack
                </Button>
                <div className="form-text">
                    {product.images.length === 0
                        ? 'Add images to the product first.'
                        : "The images above, in their order, named by the SKU, with alt-text.txt giving each one's alt text."}
                </div>

                <h3 className="h6 mt-4">Reviewer's notes</h3>
                {notes ? (
                    <p className="small mb-2">
                        <a href={notes.url} target="_blank" rel="noopener">{notes.fileName}</a> ({formatBytes(notes.fileSize)}),
                        uploaded {formatDateTime(notes.uploadedOn)}{' '}
                        <Button variant="link" size="sm" className="p-0 align-baseline text-danger" disabled={busy} onClick={() => setRemovingNotes(true)}>
                            Remove
                        </Button>
                    </p>
                ) : <p className="small text-body-secondary mb-2">None yet.</p>}
                <Form key={formKey} onSubmit={uploadNotes} className="d-flex flex-wrap gap-2 align-items-start">
                    <Form.Group controlId="press-notes-file" className="flex-grow-1">
                        <Form.Control type="file" size="sm" accept=".pdf,application/pdf" aria-label="Reviewer's notes PDF"
                                      isInvalid={!!notesProblem} aria-invalid={!!notesProblem || undefined}
                                      aria-describedby={notesProblem ? 'press-notes-error' : undefined}
                                      onChange={e => setNotesFile((e.target as HTMLInputElement).files?.[0])}/>
                        <Form.Control.Feedback type="invalid" id="press-notes-error">{notesProblem}</Form.Control.Feedback>
                    </Form.Group>
                    <Button type="submit" size="sm" variant="outline-primary" disabled={busy || !notesFile || !!notesProblem}>
                        {notes ? 'Replace' : 'Upload'}
                    </Button>
                </Form>
                {progress !== undefined && <ProgressBar className="mt-2" now={progress * 100}/>}

                <Form onSubmit={save} className="mt-4">
                    <div className="mb-3">
                        <div id="press-summary-label" className="form-label h6">Feature summary</div>
                        <RichTextEditor labelledBy="press-summary-label" value={summary} onChange={setSummary}/>
                        <div className="form-text">What the press page says about the product: a few headings and lists of its features.</div>
                    </div>
                    <Form.Check type="switch" id="press-published" className="mb-3" label="Show on press page" checked={published}
                                onChange={e => setPublished(e.target.checked)}/>
                    {published && !product.published && (
                        <div className="form-text mb-3">The product isn't published, so it won't be on the press page until it is.</div>
                    )}
                    <div className="d-flex align-items-center gap-3">
                        <SubmitButton busy={busy}>Save press kit</SubmitButton>
                        {saved && <span className="text-success small" role="status">Saved.</span>}
                    </div>
                </Form>
            </Card.Body>
            <ConfirmModal show={removingNotes} title="Remove the reviewer's notes?" confirmLabel="Remove" busy={busy}
                          onConfirm={() => { setRemovingNotes(false); void run(() => ProductsApi.removeReviewerNotes(product.id)); }}
                          onCancel={() => setRemovingNotes(false)}>
                The PDF is deleted from the press page, too.
            </ConfirmModal>
        </Card>
    );
}
